import * as Styled from "./TopControls.styles";

import { LocationFlag, useLocationFlags } from "context/LocationFlagsContext";
import { useCallback, useEffect, useState } from "react";

import { Switch } from "antd";
import { useGDA } from "context/GDAContext";
import { useLocation } from "context/LocationContext";

function TopControls() {
  const [isEnabled, setIsEnabled] = useState<boolean>(false);

  const { currentLocation, locationActions, sendCompleteOnboardingCallRequest, lastOnboardingCallRequest, onboardingAuthUrlRequest, oAuthTokenRequest, locationDetails } = useLocation();

  const [isModalCompleteOnboardingCallOpen, setIsModalCompleteOnboardingCallOpen] = useState(false);
  const [isOnboardingEnabled, setIsOnboardingEnabled] = useState(false);
  const [isLightspeedLocation, setIsLightspeedLocation] = useState(false);
  const [isSquareLocation, setIsSquareLocation] = useState(false);
  const [isOnboardingRequest24HoursOld, setIsOnboardingRequest24HoursOld] = useState(false);
  const [isOnboardingRequestCompleted, setIsOnboardingRequestCompleted] = useState(false);
  const [isModalOnboardingUrlOpen, setIsModalOnboardingUrlOpen] = useState(false);
  const [isModalOAuthTokenOpen, setIsModalOAuthTokenOpen] = useState(false);

  const [completeOnboardingButtonTitle, setCompleteOnboardingButtonTitle] = useState("");
  const [isCompleteOnboardingButtonEnabled, setCompleteOnboardingButtonEnabled] = useState(false);

  const isModalEnabled = !!lastOnboardingCallRequest;

  const { updateLocationFlags } = useLocationFlags();
  const { isGDAEnabled, setIsGDAEnabled } = useGDA();

  const is24HoursAgo = (date: Date) => {
    const hoursMillis = 1000 * 60 * 60 * 24;
    const hoursAgo = Date.now() - hoursMillis;

    return date.getTime() > hoursAgo;
  };

  const handleLocationSetup = useCallback(
      () => {
        if (currentLocation) {
          setIsLightspeedLocation(currentLocation?.posType === "lightspeed");
          setIsOnboardingEnabled(["lightspeed", "shift4"].includes(currentLocation?.posType ?? ""));
          setIsSquareLocation(currentLocation?.posType === "square");
        }
      },
      [currentLocation],
  );

  useEffect(() => {
    handleLocationSetup();
  }, [handleLocationSetup]);

  useEffect(() => {
    if (locationDetails) {
      setIsEnabled(locationDetails?.enabled);
    }
  }, [locationDetails]);

  useEffect(() => {
    if (lastOnboardingCallRequest) {
      const lastOnboardingRequestCreatedAt = new Date(lastOnboardingCallRequest?.createdAt ?? "");
      const isRequest24HoursOld = is24HoursAgo(lastOnboardingRequestCreatedAt);
      setIsOnboardingRequest24HoursOld(isRequest24HoursOld);

      const lastOnboardingRequestArray = lastOnboardingCallRequest?.onboardingStatusRequests;
      const isOnboardRequestCompleted = !lastOnboardingRequestArray ||
          lastOnboardingRequestArray?.length === 0 ||
          lastOnboardingRequestArray[0].status === "COMPLETED_ONBOARDING_REQUEST";

      setIsOnboardingRequestCompleted(isOnboardRequestCompleted);

      if (isLightspeedLocation) {
        setCompleteOnboardingButtonTitle(isOnboardingRequest24HoursOld && !isOnboardingRequestCompleted ? "Complete Onboarding" : isOnboardingRequestCompleted ? "Onboarding Complete" : "Onboarding Expired");
        setCompleteOnboardingButtonEnabled(isOnboardingRequest24HoursOld && !isOnboardingRequestCompleted);
      } else {
        setCompleteOnboardingButtonTitle(isOnboardRequestCompleted ? "Onboarding Completed" : "Complete Onboarding");
        setCompleteOnboardingButtonEnabled(!isOnboardingRequestCompleted);
      }
    }
  }, [isLightspeedLocation, isOnboardingRequest24HoursOld, isOnboardingRequestCompleted, lastOnboardingCallRequest]);

  const handleChangeEnabled = useCallback(() => {
    setIsEnabled(!isEnabled);

    locationActions.update({
      newEnabledState: !isEnabled,
    });
  }, [isEnabled, locationActions]);

  const handleCompleteOnboarding = useCallback(() => {
    sendCompleteOnboardingCallRequest({
      id: currentLocation?.id,
      onboardingCode: "DONE"
    });

    setIsModalCompleteOnboardingCallOpen(false);

  }, [currentLocation?.id, sendCompleteOnboardingCallRequest]);

  const handleUpdateGDAEnabled = useCallback(
    () => {
      const updatedLocationFlags: LocationFlag[] = [
        {
          name: "gda.enabled",
          enabled: !isGDAEnabled,
        }
      ];

      setIsGDAEnabled(!isGDAEnabled);
      void updateLocationFlags(updatedLocationFlags);
    },
    [isGDAEnabled, setIsGDAEnabled, updateLocationFlags],
  );
  

  const showCompleteOnboardingCallModal = () => {
    setIsModalCompleteOnboardingCallOpen(true);
  };

  const handleCancelOnboardingCallModal = () => {
    setIsModalCompleteOnboardingCallOpen(false);
  };

  const showOnboardingUrlModal = () => {
    setIsModalOnboardingUrlOpen(true);
  };

  const handleCancelOnboardingUrlModal = () => {
    setIsModalOnboardingUrlOpen(false);
  };

  const showOAuthTokenModal = () => {
    setIsModalOAuthTokenOpen(true);
  };

  const handleCancelOAuthTokenModal = () => {
    setIsModalOAuthTokenOpen(false);
  };

  return (
    <Styled.Container>

      {
        isSquareLocation && (
          <>
            <Styled.Button
              onClick={showOAuthTokenModal}
              type="primary"
            >
                      OAuth Token
            </Styled.Button>

            <Styled.Modal
              title="OAuth Token"
              open={isModalOAuthTokenOpen}
              onCancel={handleCancelOAuthTokenModal}
              footer={[
                <Styled.Button key="Cancel" onClick={handleCancelOAuthTokenModal}>
                            Cancel
                </Styled.Button>
              ]}
            >
              <Styled.Paragraph copyable>
                {oAuthTokenRequest?.accessToken}
              </Styled.Paragraph>
            </Styled.Modal>
          </>
        )
      }
      {
        (isLightspeedLocation || isSquareLocation) && (
          <>
            <Styled.Button
              onClick={showOnboardingUrlModal}
              type="primary"
            >
                    Onboarding Url
            </Styled.Button>

            <Styled.Modal
              title="Onboarding Auth Url"
              open={isModalOnboardingUrlOpen}
              onCancel={handleCancelOnboardingUrlModal}
              footer={[
                <Styled.Button key="Cancel" onClick={handleCancelOnboardingUrlModal}>
                          Cancel
                </Styled.Button>
              ]}
            >
              <Styled.Paragraph copyable>{onboardingAuthUrlRequest?.authUrl}</Styled.Paragraph>
            </Styled.Modal>
          </>
        )
      }
      {
        isOnboardingEnabled && isModalEnabled && (
          <>
            <Styled.Button
              onClick={showCompleteOnboardingCallModal}
              type={isCompleteOnboardingButtonEnabled ? "primary" : "link"}
            >
              {completeOnboardingButtonTitle}
            </Styled.Button>

            <Styled.Modal
              title="Complete Onboarding"
              open={isModalCompleteOnboardingCallOpen}
              onCancel={handleCancelOnboardingCallModal}
              footer={[
                <Styled.Button key="Cancel" onClick={handleCancelOnboardingCallModal}>
                          Cancel
                </Styled.Button>,
                <Styled.Button key="submit" type="primary" onClick={handleCompleteOnboarding} disabled={!isCompleteOnboardingButtonEnabled}>
                          Submit
                </Styled.Button>
              ]}
            >
              {JSON.stringify(lastOnboardingCallRequest, undefined, 4)}

            </Styled.Modal>

          </>
        )
      }

      <Styled.SwitchContainer>
        <Styled.SwitchInnerContainer>
          <div style={{ whiteSpace: "nowrap" }}>
              GDA Location
          </div>
          <Switch
            key={currentLocation?.locationId}
            checked={isGDAEnabled}
            onChange={handleUpdateGDAEnabled}
            title="Enable GDA Location"
          />
        </Styled.SwitchInnerContainer>
        <Styled.SwitchInnerContainer>
          <div style={{ whiteSpace: "nowrap" }}>
              Enable Location
          </div>
          <Switch
            key={currentLocation?.locationId}
            checked={isEnabled}
            onChange={handleChangeEnabled}
            title="Enable Location"
          />
        </Styled.SwitchInnerContainer>
      </Styled.SwitchContainer>
    </Styled.Container>
  );
}

export default TopControls;
